import { useEffect, useState } from 'react';

interface ScreenshotProps {
    filename: string;
    alt: string;
    dark: boolean;
    className?: string;
}

/**
 * Renders a screenshot from `public/assets/screenshots/`, tolerant of files
 * that don't exist yet (a separate agent captures them into that folder
 * after this site is built). Falls back dark -> light -> hidden, so the
 * page still lays out cleanly with no screenshots at all.
 */
const Screenshot: React.FC<ScreenshotProps> = ({ filename, alt, dark, className }) => {
    const lightSrc = `/assets/screenshots/${filename}`;
    const darkSrc = `/assets/screenshots/dark/${filename}`;
    const initialSrc = dark ? darkSrc : lightSrc;

    const [src, setSrc] = useState(initialSrc);
    const [triedFallback, setTriedFallback] = useState(false);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        setSrc(dark ? darkSrc : lightSrc);
        setTriedFallback(false);
        setFailed(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dark, filename]);

    if (failed) {
        return null;
    }

    return (
        <img
            className={className}
            src={src}
            alt={alt}
            loading="lazy"
            onError={() => {
                if (!triedFallback && src !== lightSrc) {
                    setTriedFallback(true);
                    setSrc(lightSrc);
                } else {
                    setFailed(true);
                }
            }}
        />
    );
};

export default Screenshot;
